<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateWorksheetsFromWasteBinReadingsModal',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    readings: {
      type: Array,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const formId = ref(null);
  const labelAlign = ref('right');
  const item = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));
  const b_7789867021953041_dataSource = computed(() => ({
    name: 'suggest_product',
    parameters: {
      part_types: 'service',
    },
  }));
  const b_7527331142178322_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      role: 'partner',
      contractors_only: true,
    },
  }));
  const b_5074589820235598_dataSource = computed(() => ({
    name: 'suggest_company_employee',
    parameters: {
      company_role: 'partner',
    },
  }));

  const $data = computed(() => ({
    formId: formId.value,
    labelAlign: labelAlign.value,
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_7789867021953041_dataSource: b_7789867021953041_dataSource.value,
    b_7527331142178322_dataSource: b_7527331142178322_dataSource.value,
    b_5074589820235598_dataSource: b_5074589820235598_dataSource.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = () => {
    return $fn.value.schemaDefaults('CreateWorksheetsFromReadings', {
      readings: props.readings,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'labelAlign' },
      { name: 'item' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_7789867021953041_dataSource', nodeId: '7789867021953041' },
      { name: 'b_7527331142178322_dataSource', nodeId: '7527331142178322' },
      { name: 'b_5074589820235598_dataSource', nodeId: '5074589820235598' },
    ],
    loaders: { formId: $$load_formId, item: $$load_item },
    ux: {},
    dataRefs: {
      formId,
      labelAlign,
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_7789867021953041_dataSource,
      b_7527331142178322_dataSource,
      b_5074589820235598_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'CreateWorksheetsFromWasteBinReadingsModal',
        'Create Worksheets'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.readings,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readings');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_1u2b2ihpq_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_ikk645mct_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };
  const act_1040167445267876_reloadGrid_saiwed384_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'wasteBinReadings',
    };
  };
  const act_1040167445267876_request_jow8ul001_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_worksheets_from_waste_bin_readings',
      data: item.value,
    };
  };

  const act_7315092382398562_closeModal_1u2b2ihpq =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_1u2b2ihpq_getActionArgs,
    });
  const inst_evh_7315092382398562_close_t2zv6ac93 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_1u2b2ihpq,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_t2zv6ac93],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_wes3130qd =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_h5k31pcmt =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_wes3130qd,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_h5k31pcmt],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_gdftzl87e =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_dtueb7saw =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_gdftzl87e,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_dtueb7saw],
    event: 'close',
  });
  const act_1040167445267876_closeModal_ikk645mct =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_ikk645mct_getActionArgs,
    });
  const inst_evh_1040167445267876_success_a0jf8f9y9 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_ikk645mct,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_reloadGrid_saiwed384 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_1040167445267876_reloadGrid_saiwed384_getActionArgs,
    });
  const inst_evh_1040167445267876_success_q00razq2u =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadGrid_saiwed384,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_1040167445267876_request_jow8ul001 = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_jow8ul001_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_1040167445267876_success_a0jf8f9y9,
      inst_evh_1040167445267876_success_q00razq2u,
    ],
  });
  const inst_evh_1040167445267876_submit_u6t0d5kh6 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_jow8ul001,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_u6t0d5kh6],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_gnlp22f77 =
    new actionTypes.CloseModalAction({});
  const inst_evh_2434514588044777_clickPreventStop_qtpt6ea49 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_gnlp22f77,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_qtpt6ea49],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_t2zv6ac93,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_h5k31pcmt,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_dtueb7saw,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_a0jf8f9y9,
    inst_evh_1040167445267876_success_q00razq2u,
    inst_evh_1040167445267876_submit_u6t0d5kh6,
    submit: inst_evh_1040167445267876_submit_u6t0d5kh6,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_qtpt6ea49,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_t2zv6ac93 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_t2zv6ac93.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_h5k31pcmt = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_h5k31pcmt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_dtueb7saw = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_dtueb7saw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_a0jf8f9y9 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_a0jf8f9y9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_q00razq2u = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_q00razq2u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_u6t0d5kh6 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_u6t0d5kh6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_qtpt6ea49 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_qtpt6ea49.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateWorksheetsFromWasteBinReadingsModal"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="CreateWorksheetsFromWasteBinReadingsModal"
        :id="formId"
        v-if="loaded"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="service_id"
          :label="
            pgettext('CreateWorksheetsFromWasteBinReadingsModal', 'Service')
          "
          :helpTooltip="
            pgettext(
              'CreateWorksheetsFromWasteBinReadingsModal',
              'Create line items with the selected service.'
            )
          "
          :labelAlign="labelAlign"
          :label-cols-md="5"
          :content-cols-md="7"
        >
          <ItpFormSearchableSelect
            name="service_id"
            v-model="item.service_id"
            :dataSource="b_7789867021953041_dataSource"
            valueField="id"
            textField="name"
            required
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
        <ItpFormGroup
          name="worksheet_description"
          :label="
            pgettext(
              'CreateWorksheetsFromWasteBinReadingsModal',
              'Worksheet description'
            )
          "
          :helpTooltip="
            pgettext(
              'CreateWorksheetsFromWasteBinReadingsModal',
              'The given value will be the description of the created worksheet. Otherwise the selected service name.'
            )
          "
          :labelAlign="labelAlign"
          :label-cols-md="5"
          :content-cols-md="7"
        >
          <ItpFormInput
            name="worksheet_description"
            v-model="item.worksheet_description"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="contractor_id"
          :label="pgettext('Worksheet', 'Contractor')"
          :labelAlign="labelAlign"
          :label-cols-md="5"
          :content-cols-md="7"
        >
          <ItpFormSearchableSelect
            name="contractor_id"
            v-model="item.contractor_id"
            :dataSource="b_7527331142178322_dataSource"
            valueField="id"
            textField="name"
            optionTemplate="<UcSuggestCompanyOption :option=option></UcSuggestCompanyOption>"
            use-option-template-for-selected-option
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
        <ItpFormGroup
          name="worker_id"
          :label="pgettext('Worksheet', 'Worker')"
          :labelAlign="labelAlign"
          :label-cols-md="5"
          :content-cols-md="7"
        >
          <ItpFormSearchableSelect
            name="worker_id"
            v-model="item.worker_id"
            :dataSource="b_5074589820235598_dataSource"
            valueField="id"
            textField="name"
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
        <ItpFormGroup
          name="gl_account_number"
          :label="
            pgettext(
              'CreateWorksheetsFromWasteBinReadingsModal',
              'GL Account Number'
            )
          "
          :labelAlign="labelAlign"
          :label-cols-md="5"
          :content-cols-md="7"
        >
          <ItpFormInput
            name="gl_account_number"
            v-model="item.gl_account_number"
          >
          </ItpFormInput>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="
            pgettext(
              'CreateWorksheetsFromWasteBinReadingsModal',
              'Create worksheets'
            )
          "
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
